import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUpdateContactStatus } from "../hooks/useContact";
import type { ContactMessage } from "../types";

interface ContactDetailModalProps {
  message: ContactMessage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ContactDetailModal = ({ message, open, onOpenChange }: ContactDetailModalProps) => {
  const { t } = useTranslation("common");
  const updateStatusMutation = useUpdateContactStatus();

  if (!message) return null;

  const handleMarkAsRead = async () => {
    await updateStatusMutation.mutateAsync({ id: message.id, status: "read" });
  };

  const handleMarkAsReplied = async () => {
    await updateStatusMutation.mutateAsync({ id: message.id, status: "replied" });
  };

  const handleReply = () => {
    const mailto = `mailto:${message.email}?subject=${encodeURIComponent(`Re: ${message.subject}`)}`;
    window.open(mailto, "_blank");
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "unread":
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30">
            {t("CONTACT.statusUnread")}
          </Badge>
        );
      case "read":
        return (
          <Badge className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30">
            {t("CONTACT.statusRead")}
          </Badge>
        );
      case "replied":
        return (
          <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
            {t("CONTACT.statusReplied")}
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-neutral-800 bg-neutral-900 text-neutral-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{message.subject}</span>
            {getStatusBadge(message.status)}
          </DialogTitle>
          <DialogDescription className="text-neutral-400">
            {t("CONTACT.from")} {message.name} ({message.email}) &middot;{" "}
            {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true, locale: es })}
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 rounded-lg bg-neutral-800/50 border border-neutral-700 p-4">
          <p className="text-neutral-200 whitespace-pre-wrap leading-relaxed">{message.message}</p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {message.status === "unread" && (
            <Button
              variant="outline"
              onClick={handleMarkAsRead}
              disabled={updateStatusMutation.isPending}
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            >
              {t("CONTACT.markAsRead")}
            </Button>
          )}
          {message.status !== "replied" && (
            <Button
              variant="outline"
              onClick={handleMarkAsReplied}
              disabled={updateStatusMutation.isPending}
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            >
              {t("CONTACT.markAsReplied")}
            </Button>
          )}
          <Button onClick={handleReply} className="bg-purple-600 text-white hover:bg-purple-700">
            {t("CONTACT.reply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDetailModal;
